import { createAction, createAsyncAction, createReducer, isActionOf } from 'typesafe-actions'
import { combineReducers } from 'redux'
import { createProjectFilterReducer, ProjectFiltersState } from './project-list-filters'
import { taskStatusFilters } from '../../components/pages/projects/ProjectList/ProjectFilters/filters'
import { SortDirection } from '../../constants/direction'
import { combineEpics } from 'redux-observable'
import { projectActions } from '../data/project/project-actions'
import { RequestStatus } from '../../responses/response'
import { createSelectAction, payloadReducer, statusReducer } from '../../utils/redux-utils'
import { ID } from '../../types/UtilityTypes'
import { getId, idToggle } from '../../utils/entity-utils'
import { ADTType, makeADT, ofType } from '@morphic-ts/adt'
import { Project, ProjectStatus, ServerProject } from '../../responses/projects'
import type { RootEpic } from '../root'
import { apiRequests } from '../../http/config'
import { filter, map, mergeMap } from 'rxjs/operators'
import { toastFailedResponse } from '../../components/controls/Toaster/Toaster'
import { pipe } from 'fp-ts/function'
import { projectsSelector } from '../../utils/selectors'
import * as A from 'fp-ts/Array'
import { enumIs } from '../../utils/enum-utils'
import { from, of } from 'rxjs'
import { push } from 'connected-react-router'
import { urlWithParams } from '../../http/utils'
import { ROUTES } from '../../http/routes'
import { archiveStatusFilters } from '../../components/pages/archive/ArchivePage/config'

export const ArchiveEditState = makeADT('type')({
    none: ofType<{ type: 'none' }>(),
    archive: ofType<{ type: 'archive', projects: ID[] }>(),
    unarchive: ofType<{ type: 'unarchive', project: Project }>(),
    reserve: ofType<{ type: 'reserve' }>(),
})

export const archiveActions = {
    updateFilters: createAction('@ARCHIVE/UPDATE_FILTERS')<Partial<ProjectFiltersState>>(),
    clearFilters: createAction('@ARCHIVE/CLEAR_FILTERS')(),
    startArchiving: createAction('@ARCHIVE/START_ARCHIVING')<ID[]>(),
    startUnarchiving: createAction('@ARCHIVE/START_UNARCHIVING')<Project>(),
    startReserving: createAction('@ARCHIVE/START_RESERVING')(),
    cancelEdit: createAction('@ARCHIVE/CANCEL_EDIT')(),
    selectedProjects: createSelectAction(
        'ARCHIVE/SELECTED_SET',
        '@ARCHIVE/SELECTED_TOGGLE',
        '@ARCHIVE/SELECTED_TOGGLE_ALL',
    ),
    archive: createAsyncAction(
        '@ARCHIVE/ARCHIVE_REQUEST',
        '@ARCHIVE/ARCHIVE_SUCCESS',
        '@ARCHIVE/ARCHIVE_FAILURE',
    )<ID[], ServerProject[], void>(),
    unarchive: createAsyncAction(
        '@ARCHIVE/UNARCHIVE_REQUEST',
        '@ARCHIVE/UNARCHIVE_SUCCESS',
        '@ARCHIVE/UNARCHIVE_FAILURE',
    )<Project, ServerProject[], void>(),
    reserve: createAsyncAction(
        '@ARCHIVE/RESERVE_REQUEST',
        '@ARCHIVE/RESERVE_SUCCESS',
        '@ARCHIVE/RESERVE_FAILURE',
    )<undefined, ServerProject[], void>()
}

export interface ArchiveState {
    editState: ADTType<typeof ArchiveEditState>,
    status: RequestStatus,
    reserveStatus: RequestStatus,
    total: number,
    selected: ID[],
    projects: ID[],
    filters: ProjectFiltersState,
    reservable: ID[],
    streamerInfo: {
        min: number,
        max: number,
        number: string,
    },
}

export const archiveReducer = combineReducers<ArchiveState>({
    editState: createReducer<ADTType<typeof ArchiveEditState>>(ArchiveEditState.as.none({}))
        .handleAction([
            archiveActions.cancelEdit,
            archiveActions.archive.success,
            archiveActions.reserve.success,
            archiveActions.unarchive.success,
        ], () => ArchiveEditState.as.none({}))
        .handleAction(archiveActions.startArchiving, (_, action) => ArchiveEditState.as.archive({projects: action.payload}))
        .handleAction(archiveActions.startReserving, () => ArchiveEditState.as.reserve({}))
        .handleAction(archiveActions.startUnarchiving, (_, action) => ArchiveEditState.as.unarchive({project: action.payload})),
    status: statusReducer({
        request: projectActions.archive.init,
        success: projectActions.archive.success,
        failure: projectActions.archive.failure
    }),
    reserveStatus: statusReducer({
        request: projectActions.reservable.init,
        success: projectActions.reservable.success,
        failure: projectActions.reservable.failure,
    }),
    total: createReducer(0)
        .handleAction(projectActions.archive.success, (_, action) => action.payload.total),
    selected: createReducer<ID[]>([])
        .handleAction([archiveActions.archive.success, archiveActions.reserve.success], () => [])
        .handleAction(archiveActions.selectedProjects.set, payloadReducer)
        .handleAction(archiveActions.selectedProjects.toggle, (state, action) => idToggle(action.payload)(state)),
    projects: createReducer<ID[]>([])
        .handleAction(projectActions.archive.success, (state, action) =>
            action.payload.offset === 0 ? action.payload.projects.map(getId) : state.concat(action.payload.projects.map(getId))
        ),
    filters: createProjectFilterReducer({
        dropdowns: {},
        openedAt: [undefined, undefined],
        projectStatus: archiveStatusFilters,
        sortField: null,
        sortDirection: SortDirection.ASC,
        taskStatus: taskStatusFilters[0],
        search: '',
        tags: [],
        object_design_status: null,
        object_production_status: null
    }, {
        clear: archiveActions.clearFilters,
        update: archiveActions.updateFilters,
    }),
    reservable: createReducer<ID[]>([])
        .handleAction(projectActions.reservable.init, () => [])
        .handleAction(projectActions.reservable.success, (state, {payload}) => state.concat(payload.rows.map(getId))),
    streamerInfo: createReducer({min: 0, max: 0, number: '...'})
        .handleAction(projectActions.reservable.success, (_, {payload}) => ({
            min: payload.min_size.value,
            max: payload.max_size.value,
            number: payload.reserve_disk_number,
        })),
})

const archive: RootEpic = action$ => action$.pipe(
    filter(isActionOf(archiveActions.archive.request)),
    mergeMap(action => apiRequests.project.archive({
        data: {id: action.payload},
    })),
    map(res => res.response.success ?
        archiveActions.archive.success(res.response.rows) :
        archiveActions.archive.failure(toastFailedResponse(res.response, 'Архивация не удалась'))
    )
)

const unarchive: RootEpic = action$ => action$.pipe(
    filter(isActionOf(archiveActions.unarchive.request)),
    mergeMap(action => apiRequests.project.unarchive({
        data: {id: [action.payload.id]},
    }).pipe(
        mergeMap(res => res.response.success ?
            from([archiveActions.unarchive.success(res.response.rows), push(urlWithParams({projectId: action.payload.id})(ROUTES.PROJECT_DETAIL))]) :
            of(archiveActions.unarchive.failure(toastFailedResponse(res.response, 'Разархивация не удалась')))

        )
    )),
)

const refresh: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf([archiveActions.archive.success, archiveActions.unarchive.success])),
    map(() => projectActions.archive.init(state$.value.pages.archive.filters))
)

const isSelected = enumIs()([ProjectStatus.TO_ARCHIVE, ProjectStatus.CLOSED])

const selectAll: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(archiveActions.selectedProjects.toggleAll)),
    map(() => {
        const {projects, selected} = state$.value.pages.archive
        const toArchive = pipe(
            state$.value,
            projectsSelector(projects),
            A.filter(project => isSelected(project.status_id)),
            A.map(getId),
        )
        return archiveActions.selectedProjects.set(toArchive.length === selected.length ? [] : toArchive)
    })
)

export const archiveEpic = combineEpics(archive, unarchive, refresh, selectAll)